import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useScopedClient } from "@/features/clients/hooks/use-scoped-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SessionsAPI } from "../api/sessions";
import type {
  AdminSendAs,
  SearchSessionsOptions,
  SearchSessionsResult,
  Session,
  SessionListItem,
  SessionStats,
} from "../types";
import { isDemoMode } from "@/features/utils/demo-mode";
import {
  mockSessionStats,
  mockSearchSessionsResult,
  getMockSession,
} from "./use-sessions.mock";

// Fetch all sessions (deprecated - use useSearchSessions instead)
export function useSessions(options?: {
  customer_id?: string;
  agent_id?: string;
  state?: "open" | "closed";
}) {
  const clientId = useCurrentClient()?.client?.id || "";
  return useQuery({
    queryKey: ["sessions", clientId, options],
    queryFn: (): Promise<SessionListItem[]> =>
      SessionsAPI.list(clientId, options),
    enabled: !!clientId,
  });
}

// Search sessions with advanced filtering
//
// This one, the stats and a single session also serve the client's file in the
// platform back-office (`useScopedClient`): the scope is part of each key, last,
// so the prefixes the mutations below invalidate still match.
export function useSearchSessions(
  options?: SearchSessionsOptions,
  settings?: { enabled?: boolean },
) {
  const { clientId, scope } = useScopedClient();
  return useQuery({
    queryKey: ["sessions", clientId, options, scope],
    queryFn: (): Promise<SearchSessionsResult> => {
      // Return mock data in demo mode
      if (isDemoMode()) {
        return Promise.resolve(mockSearchSessionsResult);
      }
      return SessionsAPI.search(clientId, options, scope);
    },
    enabled: !!clientId && settings?.enabled !== false,
  });
}

// Fetch stats
export function useSessionStats(options?: {
  date_from?: string;
  date_to?: string;
  /** IANA zone `sessions_by_day` is counted in (UTC when absent). */
  timezone?: string;
}) {
  const { clientId, scope } = useScopedClient();
  return useQuery({
    queryKey: ["sessions", clientId, "stats", options, scope],
    queryFn: (): Promise<SessionStats> => {
      // Return mock data in demo mode
      if (isDemoMode()) {
        return Promise.resolve(mockSessionStats);
      }
      return SessionsAPI.stats(clientId, options, scope);
    },
    enabled: !!clientId,
  });
}

// Fetch a single session
export function useSession(sessionId: string) {
  const { clientId, scope } = useScopedClient();
  return useQuery({
    // Do not use the entity name to avoid reloading the session at every message (other websocket channel for that)
    queryKey: ["sessions-single", clientId, sessionId, scope],
    queryFn: (): Promise<Session> => {
      // Return mock data in demo mode
      if (isDemoMode()) {
        return Promise.resolve(getMockSession(sessionId));
      }
      return SessionsAPI.get(clientId, sessionId, scope);
    },
    enabled: !!sessionId && !!clientId,
  });
}

// An administrator writing into a conversation (SYSADMIN): see `SessionsAPI.adminSend`.
export function useAdminSendSessionMessage() {
  const clientId = useCurrentClient()?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      message,
      as,
    }: {
      sessionId: string;
      message: string;
      as: AdminSendAs;
    }): Promise<Session> =>
      SessionsAPI.adminSend(clientId, sessionId, message, as),
    onSuccess: (_data, variables) => {
      // Refetched rather than overwritten: the reply is the session without the
      // history (previous sessions, transfer chain) the thread is built from. The
      // message itself is already on screen, from the socket.
      queryClient.invalidateQueries({
        queryKey: ["sessions-single", clientId, variables.sessionId],
      });
    },
  });
}

// A person of the team answering a Meta conversation: see `SessionsAPI.operatorMessage`.
export function useOperatorMessage() {
  const clientId = useCurrentClient()?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      message,
    }: {
      sessionId: string;
      message: string;
    }): Promise<Session> =>
      SessionsAPI.operatorMessage(clientId, sessionId, message),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["sessions-single", clientId, variables.sessionId],
      });
    },
  });
}

// Send a message to a session
export function useSendSessionMessage() {
  const clientId = useCurrentClient()?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      message,
    }: {
      sessionId: string;
      message: string;
    }): Promise<Session> =>
      SessionsAPI.sendMessage(clientId, sessionId, message),
    onSuccess: (data, variables) => {
      // Update the cache with the response data
      queryClient.setQueryData(["sessions", variables.sessionId], data);
      // Invalidate sessions list only
      queryClient.invalidateQueries({
        queryKey: ["sessions"],
        exact: true,
      });
    },
  });
}
